import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, Share2 } from "lucide-react";
import SignalDots from "@/components/SignalDots";
import InitialsAvatar from "@/components/InitialsAvatar";
import { formatRelativeDate } from "@/lib/utils";

export default function ConvergenceRow({ item, index }) {
  const onShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/ticker/${item.ticker}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch {
      toast.error("Could not copy link");
    }
  };

  const pills = (
    <div className="flex flex-wrap items-center gap-1.5 md:max-w-[380px]">
      {item.experts.slice(0, 6).map((e) => (
        <span
          key={e.id}
          title={e.is_insider ? `${e.name} (insider — reduced weight)` : e.name}
          className={`inline-flex items-center gap-1.5 border py-0.5 pl-0.5 pr-2 ${
            e.is_insider
              ? "border-[#334155] bg-[#0b1120] opacity-70"
              : "border-[#1e293b] bg-[#0f172a]"
          }`}
        >
          <InitialsAvatar name={e.name} expertId={e.id} size="xs" />
          <span className="text-[11px] font-medium text-[#cbd5e1]">{e.name}</span>
          {e.is_insider && (
            <span className="font-mono text-[8px] uppercase tracking-wider text-[#f59e0b]">
              insider
            </span>
          )}
        </span>
      ))}
    </div>
  );

  const shareBtn = (
    <button
      type="button"
      onClick={onShare}
      data-testid={`share-btn-${item.ticker}`}
      aria-label={`Share ${item.ticker} signal`}
      className="flex h-8 w-8 shrink-0 items-center justify-center border border-[#1e293b] text-[#64748b] transition-colors hover:border-[#3b82f6] hover:text-[#60a5fa]"
    >
      <Share2 className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <Link
      to={`/ticker/${item.ticker}`}
      data-testid={`convergence-row-${item.ticker}`}
      className="se-fade-in group block border-b border-[#1e293b] bg-[#111827] px-4 py-4 transition-colors hover:bg-[#0f172a] md:grid md:grid-cols-[64px_1fr_auto_auto_auto] md:items-center md:gap-4 md:px-5"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* ============ MOBILE LAYOUT (<md) ============ */}
      <div className="md:hidden">
        {/* Row 1: rank + ticker + share */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-baseline gap-2">
            <span className="font-mono text-[10px] text-[#475569] tabular">
              #{String(index + 1).padStart(2, "0")}
            </span>
            <span className="mono text-lg font-bold tracking-tight text-[#f8fafc]">
              {item.ticker}
            </span>
            <span className="truncate text-xs text-[#94a3b8]">
              {item.company}
            </span>
          </div>
          {shareBtn}
        </div>

        {/* Row 2: latest date */}
        <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
          latest {formatRelativeDate(item.latest_date)}
        </div>

        {/* Row 3: expert pills */}
        <div className="mt-3">{pills}</div>

        {/* Row 4: weight + voices + signal dots */}
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-[#1e293b] pt-3">
          <div className="flex items-center gap-4">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#64748b]">
                weight
              </div>
              <div
                className="mono text-base font-bold text-[#f8fafc] tabular"
                data-testid={`weighted-score-mobile-${item.ticker}`}
              >
                {item.weighted_score.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#64748b]">
                voices
              </div>
              <div className="mono text-base font-bold text-[#f8fafc] tabular">
                {item.voice_count}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <SignalDots strength={item.signal_strength} />
            <ChevronRight className="h-4 w-4 text-[#334155]" />
          </div>
        </div>
      </div>

      {/* ============ DESKTOP LAYOUT (md+) ============ */}
      <div className="hidden font-mono text-xs text-[#475569] tabular md:block">
        #{String(index + 1).padStart(2, "0")}
      </div>
      <div className="hidden min-w-0 md:block">
        <div className="flex items-baseline gap-3">
          <span className="mono text-lg font-bold tracking-tight text-[#f8fafc]">
            {item.ticker}
          </span>
          <span className="truncate text-sm text-[#94a3b8]">{item.company}</span>
        </div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
          latest {formatRelativeDate(item.latest_date)}
        </div>
      </div>
      <div className="hidden md:block">{pills}</div>
      <div className="hidden items-center gap-3 md:flex">
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-wider text-[#64748b]">
            weight
          </div>
          <div
            className="mono text-lg font-bold text-[#f8fafc] tabular"
            data-testid={`weighted-score-${item.ticker}`}
          >
            {item.weighted_score.toFixed(1)}
          </div>
          <div className="mt-0.5 font-mono text-[9px] uppercase tracking-wider text-[#475569]">
            {item.voice_count} voices
          </div>
        </div>
        <SignalDots strength={item.signal_strength} />
      </div>
      <div className="hidden items-center gap-1 md:flex">
        {shareBtn}
        <ChevronRight className="h-4 w-4 text-[#334155] group-hover:text-[#3b82f6]" />
      </div>
    </Link>
  );
}
